import axios from "axios";

// Runs the wallet-login signature challenge described in
// backend/routes/userRoutes.js (see F-1 in SECURITY_AUDIT_REPORT.md):
//   1. Ask the backend for a fresh, single-use nonce for this address.
//   2. Have the connected wallet sign the exact message the backend hands
//      back (this is what shows up in the MetaMask/XDCPay signing prompt).
//   3. Return the signature so the caller can submit
//      { walletAddress, signature } to /api/user/walletlogin or
//      /api/user/wallet — the backend re-derives the signer from the
//      signature and rejects anything that doesn't match.
//
// Throws if the user rejects the signature prompt or the nonce request
// fails — callers should catch this the same way they already catch a
// rejected wallet connection.
export const signWalletLoginChallenge = async (signer, walletAddress) => {
  const { data } = await axios.get("/api/user/wallet-nonce", {
    params: { walletAddress },
  });
  const signature = await signer.signMessage(data.message);
  return signature;
};

// Module-scoped (singleton, since JS modules are cached) lock around the
// nonce -> sign -> submit sequence. There are THREE independent places
// that can kick off that sequence for the same wallet — the explicit
// "Connect Wallet" click handler (web3modal/index.js), the silent
// auto-reconnect-on-mount effect (App.js), and the MetaMask
// `accountsChanged` listener (App.js) — and the last one fires as a
// side effect of the FIRST two: calling eth_requestAccounts to connect a
// wallet that has never authorized this site before itself emits
// `accountsChanged`, which the listener reacts to by independently
// re-running the whole flow.
//
// Nonces are single-use and keyed only by wallet address (see
// GET /wallet-nonce in userRoutes.js), so two concurrent runs for the
// same address stomp on each other: whichever call's GET /wallet-nonce
// lands second overwrites the first's nonce in the DB, and/or whichever
// call's submit lands first deletes the nonce outright — so the other
// call's signature (over a nonce that's now gone or replaced) always
// fails verification ("Invalid signature" / "No pending sign-in request
// for this wallet, or it expired"). The user also gets asked to sign
// TWICE in quick succession, which is confusing even when it doesn't
// outright fail.
//
// This lock makes sure only one nonce -> sign -> submit sequence is ever
// in flight at a time, from any trigger. A caller that finds the lock
// already held just backs off silently — a flow is already handling
// this wallet, there's nothing useful for a second one to do.
let walletAuthInFlight = false;

export const runWalletAuthOnce = async (fn) => {
  if (walletAuthInFlight) {
    return { skipped: true };
  }
  walletAuthInFlight = true;
  try {
    const result = await fn();
    return { skipped: false, result };
  } finally {
    walletAuthInFlight = false;
  }
};